import { useState, useEffect } from 'react';
import { DialogContent, DialogTitle, Grid, TextField, Autocomplete, FormControl } from '@mui/material';
import { FormControlLabel, Select, MenuItem, Typography, Checkbox, InputLabel } from '@mui/material';
import { isEmpty } from 'lodash';
import axios from 'axios';

import { RoundedDialog, StandardDialogActions } from '../../Global/Components';
import Uploader from '../../Global/Uploader';
import FileViewer from '../../Global/FileViewer';
import StateManager from '../../Global/StateManager';

export default function EditPost({ open, onClose, post, tags, categories }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    content: '',
    videoUrl: '',
    coverImage: '',
    tags: [],
    metaTitle: '',
    metaDescription: '',
    metaKeywords: [],
    publish: 'draft',
    showAtHome: false,
  });

  const [formFilled, setFormFilled] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (post) {
      setFormData({
        title: post.title || '',
        description: post.description || '',
        category: post.category || '',
        content: post.content || '',
        videoUrl: post.videoUrl || '',
        coverImage: post.coverImage || '',
        tags: post.tags || [],
        publish: post.publish || 'draft',
        showAtHome: post.showAtHome || false,
      });
    }
  }, [post, open]);

  useEffect(() => {
    const requiredFields = [formData.title, formData.description, formData.content, formData.coverImage];
    setFormFilled(requiredFields.every((field) => Boolean(field)));
  }, [formData]);

  const handleChange = (e) => {
    if (e.target.name === 'showAtHome') {
      setFormData({ ...formData, [e.target.name]: e.target.checked });
      return;
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTagsChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      tags: newValue,
    }));
  };

  const handleTagInputKeyDown = (event) => {
    if (event.key === 'Enter' && inputValue.trim()) {
      event.preventDefault();
      const newTag = inputValue.trim();
      const newTags = [...formData.tags, newTag];

      axios
        .post('/help/addTag', { newTags: [newTag] })
        .then(() => {
          setFormData((prev) => ({
            ...prev,
            tags: newTags,
          }));
          setInputValue('');
        })
        .catch((error) => StateManager.setAxiosErrorAlert(error));
    }
  };

  const handleImageUpload = (result, deleteMode = false) => {
    if (result.file?.location) {
      setFormData({ ...formData, coverImage: result.file.location });
    }
    if (deleteMode) {
      setFormData({ ...formData, coverImage: '' });
    }
  };

  const handleSubmit = () => {
    const existingTagTitles = tags.map((tag) => tag.title.toLowerCase());
    const newTags = formData.tags
      .filter((tag) => !existingTagTitles.includes(tag.toLowerCase()))
      .map((tag) => ({ title: tag }));

    onClose(true, { ...formData, newTags });
    setFormData({
      title: '',
      description: '',
      category: '',
      content: '',
      videoUrl: '',
      coverImage: '',
      tags: [],
      publish: 'draft',
      showAtHome: false,
    });
  };

  return (
    <RoundedDialog open={open} onClose={() => onClose(false)} maxWidth="md" className="scroll-bar">
      <DialogTitle>{`${post ? 'Edit' : 'New'} video`}</DialogTitle>
      <DialogContent className="scroll-bar">
        <Grid container spacing={3} sx={{ pt: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Title" name="title" value={formData.title} onChange={handleChange} required />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={formData.publish} label="Status" name="publish" onChange={handleChange}>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Video URL" name="videoUrl" value={formData.videoUrl} onChange={handleChange} />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={formData.category} label="Category" name="category" onChange={handleChange}>
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {categories &&
                  Object.entries(categories).map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Uploader
              onResult={handleImageUpload}
              onlyImages
              maxFiles={1}
              uploaded={formData.coverImage ? [{ location: formData.coverImage }] : []}
              buttonText="Upload Cover"
              disableLock
              showLock={false}
              documentCategory="images"
            />
          </Grid>

          {!isEmpty(formData.coverImage) && (
            <Grid container item sx={{ mt: 1 }}>
              <FileViewer
                files={[
                  {
                    location: formData.coverImage,
                    mimetype: 'image/jpeg',
                    originalname: 'cover image',
                    size: 0,
                  },
                ]}
                title=""
                onDelete={() => {
                  handleImageUpload({}, true);
                }}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              multiline
              rows={5}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              freeSolo
              options={tags.map((tag) => tag.title)}
              getOptionLabel={(option) => option}
              value={formData.tags}
              onChange={handleTagsChange}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
              renderInput={(params) => (
                <TextField {...params} label="Tags" placeholder="Add tag" onKeyDown={handleTagInputKeyDown} />
              )}
            />
            <Typography variant="body2" color="textSecondary">
              If you want to add a new tag, enter a value and then press "Enter"
            </Typography>
          </Grid>
          <FormControlLabel
            control={<Checkbox checked={Boolean(formData.showAtHome)} onChange={handleChange} name={'showAtHome'} />}
            label={'Show at Homepage?'}
            sx={{ m: 'auto' }}
          />
        </Grid>
      </DialogContent>
      <StandardDialogActions onClose={onClose} disableDone={!formFilled} onDone={handleSubmit} />
    </RoundedDialog>
  );
}
